/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Badge,
    Button, ListGroup, ListGroupItem,
    UncontrolledAccordion
} from "reactstrap";
import { logoutPowerBIEmbedInfo } from "../../../actions/Auth/actions";
import {
    listTenants
} from "../../../actions/Tenants/actions";
import { AvosAction } from "../../../actions/types";
import { VERSION_TAG } from "../../../constants/urls";
import { getLocalHistoryRoute, useLocalHistory } from "../../../context/LocalHistory";
import { getFilledArrayOrDefault } from "../../../utils";
import { FormContainer } from "../../Helper/FormContainer";
import { Loading } from "../../Helper/Loading";
import { SearchInput } from "../../Helper/SearchInput";
import FullPageLayout from "../Layout/FullPageLayout";
import TenantCreateForm from "./TenantCreateForm";

type TenantItemProps = {
    tenant: {
        tenant_name: string;
        tenant_id: string;
        active: boolean;
    };
    isFirstItem: boolean;
}

function TenantItem({ tenant, isFirstItem }: TenantItemProps) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // const user = useSelector((state: any) => state.auth.user);

    return (
        <ListGroupItem className={`border-0 ${isFirstItem ? "border-top" : ""}`} key={`tenant-${tenant.tenant_id}`}>
            <div className="d-flex justify-content-between align-items-center">
                <div>
                    <strong>{tenant.tenant_name}</strong> {tenant.active && <Badge size="sm" color="primary">Active</Badge>}
                    <br></br>{tenant.tenant_id}
                </div>

                <div className="d-flex">
                    <Button
                        color="primary"
                        className="me-1"
                        outline
                        onClick={() => navigate(`/tenants/${tenant.tenant_id}`)}
                    >
                        Edit
                    </Button>

                    <Button
                        color="warning"
                        outline
                        onClick={() => {
                            dispatch(logoutPowerBIEmbedInfo());
                            navigate(`/${tenant.tenant_id}`);
                        }}
                    >
                        Test
                    </Button>

                    {/* Do we need this? */}
                    {/* {false && user.tenant.tenant_id === "a6aff297-ac1b-4e81-88a6-420591d31f31" && (
                        <Button
                            color="danger"
                            outline
                            onClick={() => dispatch(deleteTenant(tenant.tenant_id) as any)}
                        >
                            Delete
                        </Button>
                    )} */}
                </div>
            </div>
        </ListGroupItem>
    );
}

function Tenants() {
    const tenants = useSelector((state: any) => state.tenants);
    const user = useSelector((state: any) => state.auth.user);
    const dispatch = useDispatch();
    const { isLoading, items } = tenants;
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        dispatch(listTenants() as AvosAction);
    }, [dispatch]);

    const localHistory = useLocalHistory();

    const filteredItems = getFilledArrayOrDefault(items).filter(
        (item: any) => item.tenant_name.toLowerCase().includes(searchQuery.toLowerCase())
            || item.tenant_id.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

    const activeTenants = filteredItems.filter((item: any) => item.active);
    const otherTenants = filteredItems.filter((item: any) => !item.active);

    if (!user.is_admin) {
        return null;
    }

    if (isLoading && !items) {
        return <div css={css`min-height: 100vh;`}><Loading /></div>;
    }

    return (
        <FullPageLayout className="bg-gray">
            <FormContainer
                backTo={getLocalHistoryRoute(localHistory, "/tenants")}
                title={`Hi ${user.first_name}!`}
                subtitle={<span>You are running version <b>{VERSION_TAG}</b>, and logged in as <b>{user.username}</b></span>}
            >
                <div className="mb-3">
                    <SearchInput
                        onSearch={(value) => setSearchQuery(value || "")}
                        placeholder="Search by name or tenant ID"
                        maxWidth="100%"
                    />
                </div>

                {/* @ts-expect-error I guess reactstrap messed up */}
                <UncontrolledAccordion defaultOpen={["1", "2"]} stayOpen toggle={null}>
                    {filteredItems && filteredItems.length > 0 && (
                        <>
                            {activeTenants.length > 0 && (
                                <AccordionItem>
                                    <AccordionHeader targetId="1">
                                        <span className="fs-6 fw-bold">Active Tenants ({activeTenants.length})</span>
                                    </AccordionHeader>

                                    <AccordionBody accordionId="1">
                                        <ListGroup className="border-0">
                                            {activeTenants.map((item, idx) => (
                                                <TenantItem
                                                    isFirstItem={idx === 0}
                                                    key={`tenant-${item.tenant_id}`}
                                                    tenant={item}
                                                />
                                            ))}
                                        </ListGroup>
                                    </AccordionBody>
                                </AccordionItem>
                            )}

                            {otherTenants.length > 0 && (
                                <AccordionItem>
                                    <AccordionHeader targetId="2">
                                        <span className="fs-6 fw-bold">Other Tenants ({otherTenants.length})</span>
                                    </AccordionHeader>

                                    <AccordionBody accordionId="2">
                                        <ListGroup>
                                            {otherTenants.map((item, idx) => (
                                                <TenantItem
                                                    isFirstItem={idx === 0}
                                                    key={`tenant-${item.tenant_id}`}
                                                    tenant={item}
                                                />
                                            ))}
                                        </ListGroup>
                                    </AccordionBody>
                                </AccordionItem>
                            )}
                        </>
                    )}
                    {filteredItems && filteredItems.length === 0 && searchQuery && (
                        <div className="text-muted text-center py-4">No tenants found matching &quot;{searchQuery}&quot;</div>
                    )}
                </UncontrolledAccordion>

                <div className="mt-3">
                    <TenantCreateForm />
                </div>
            </FormContainer>
        </FullPageLayout>
    );
}

export default Tenants;
